// Product pricing helpers - centralized pricing from database
import { getTodayInAEST } from '../utils/helpers';

// ============================================
// PRICING CACHE — loaded from /api/config/pricing-all
// ============================================
let _pricingData = null;
let _pricingLoaded = false;

export const setPricingData = (data) => {
  _pricingData = data;
  _pricingLoaded = true;
};

export const isPricingLoaded = () => _pricingLoaded;

export const getViewingRoomPrice = (duration) => {
  if (!duration) return 0;
  const vrItems = _pricingData?.viewing_room;
  if (vrItems) {
    const match = vrItems.find(v => v.name === duration);
    if (match) return match.price;
  }
  if (duration === '30 minutes') return 150;
  if (duration === '45 minutes') return 180;
  if (duration === '60 minutes') return 180;
  return 0;
};

export const getViewingRoomOptions = () => {
  const vrItems = _pricingData?.viewing_room;
  if (vrItems && vrItems.length > 0) {
    return vrItems.map(v => ({ name: v.name, price: v.price }));
  }
  return [{ name: '30 minutes', price: 150 }, { name: '45 minutes', price: 180 }, { name: '60 minutes', price: 180 }];
};

export const getCertificatePrice = (certType) => {
  if (!certType) return 0;
  const items = _pricingData?.certificates;
  if (items) {
    const match = items.find(c => c.name === certType);
    if (match) return match.price;
  }
  return certType === 'Printed' ? 15 : 0;
};

export const initializePricing = async (apiUrl) => {
  try {
    const resp = await fetch(`${apiUrl}/api/config/pricing-all`);
    if (resp.ok) {
      const data = await resp.json();
      setPricingData(data);
      return true;
    }
  } catch (e) {
    console.error('Failed to load pricing data:', e);
  }
  return false;
};

export const getAvailableUrns = (petType, weightKg, gender) => {
  const weight = parseFloat(weightKg) || 0;
  
  if (petType === 'Cat') {
    // Cats always see small urn range
    return {
      "Pouch + Candle": gender === 'M' ? ["Blue Pouch + Candle"] : gender === 'F' ? ["Pink Pouch + Candle"] : ["Blue Pouch + Candle", "Pink Pouch + Candle"],
      "Ceramic – Small": ["Heavenly Pink", "Baby Blue", "Cotton White", "Aqua Blue", "Pebble Black", "Sage Green"],
      "Bamboo": ["Petite (Whitewashed)", "Small (Whitewashed)", "Small (Natural)"],
      "Concrete": ["Small (Black)", "Small (White)"],
      "Frame": ["Small"],
      "MEMORIES BOX": ["Small"]
    };
  }
  
  if (petType === 'Exotic Pet') {
    // Only mini urns for exotic pets
    return {
      "Pouch + Candle": gender === 'M' ? ["Blue Pouch + Candle"] : gender === 'F' ? ["Pink Pouch + Candle"] : ["Blue Pouch + Candle", "Pink Pouch + Candle"],
      "Exotic Pet Mini Urns": [
        "Exotic Wings glass mini urn",
        "Exotic Bamboo mini urn", 
        "Exotic Mini ceramic urn - Blue", 
        "Exotic Mini ceramic urn - White",
        "Exotic Mini ceramic urn - Green",
        "Exotic Mini ceramic urn - Yellow",
        "Exotic Mini ceramic urn - Orange"
      ]
    };
  }
  
  if (petType === 'Dog') {
    const availableUrns = {
      "Pouch + Candle": gender === 'M' ? ["Blue Pouch + Candle"] : gender === 'F' ? ["Pink Pouch + Candle"] : ["Blue Pouch + Candle", "Pink Pouch + Candle"]
    };
    
    if (weight <= 10) {
      // 0-10 kg: Small urns
      availableUrns["Ceramic – Small"] = ["Heavenly Pink", "Baby Blue", "Cotton White", "Aqua Blue", "Pebble Black", "Sage Green"];
      availableUrns["Bamboo"] = ["Petite (Whitewashed)", "Small (Whitewashed)", "Small (Natural)"];
      availableUrns["Concrete"] = ["Small (Black)", "Small (White)"];
      availableUrns["Frame"] = ["Small"];
      availableUrns["MEMORIES BOX"] = ["Small"];
    } else if (weight <= 20) {
      // 11-20 kg: Small ceramics, small bamboos, medium concrete
      availableUrns["Ceramic – Small"] = ["Heavenly Pink", "Baby Blue", "Cotton White", "Aqua Blue", "Pebble Black", "Sage Green"];
      availableUrns["Bamboo"] = ["Small (Whitewashed)", "Small (Natural)"];
      availableUrns["Concrete"] = ["Medium (Black)", "Medium (White)"];
      availableUrns["Frame"] = ["Large"];
      availableUrns["MEMORIES BOX"] = ["Medium"];
    } else {
      // Over 20 kg: Large urns
      availableUrns["Ceramic – Large"] = ["Dusk Blue", "Sage Green", "Cotton White", "Aqua Blue"];
      availableUrns["Bamboo"] = ["Medium (Whitewashed)", "Large (Whitewashed)", "Large (Natural)"];
      availableUrns["Concrete"] = ["Medium (Black)", "Medium (White)", "Large (Black)", "Large (White)"];
      availableUrns["Frame"] = ["Large"];
      availableUrns["MEMORIES BOX"] = ["Large"];
    }
    
    return availableUrns;
  }
  
  // Default fallback - return all urns
  return {};
};

export const getAvailableKeepsakes = (petType, weightKg, exoticSubcategory) => {
  const weight = parseFloat(weightKg) || 0;
  
  if (petType === 'Exotic Pet') {
    if (exoticSubcategory === 'Birds') {
      return [
        "Feather arrangement & footprint card",
        "Framed feather arrangement & footprint"
      ];
    } else if (exoticSubcategory === 'Rodents') {
      return [
        "Keepsake duo (Fur & Paw)"
      ];
    } else if (exoticSubcategory === 'Reptiles') {
      return [
        "Paw Print Card",
        "Frame Paw Print"
      ];
    } else {
      // Other exotic pets - no fur, paw, nose or feather keepsakes
      return [];
    }
  }
  
  if (petType === 'Cat') {
    return [
      "Fur Clipping x1",
      "Fur Clipping x2", 
      "Fur Clipping x3",
      "Paw Print Card",
      "Nose Print Card",
      "Frame Nose Print",
      "Frame Paw Print",
      "Keepsake duo (Fur & Nose)",
      "Keepsake duo (Fur & Paw)",
      "Clay Round Impression",
      "Clay Frame Impression"
    ];
  }
  
  if (petType === 'Dog') {
    const baseKeepsakes = [
      "Fur Clipping x1",
      "Fur Clipping x2", 
      "Fur Clipping x3",
      "Paw Print Card",
      "Nose Print Card",
      "Frame Nose Print",
      "Frame Paw Print",
      "Clay Round Impression",
      "Clay Frame Impression"
    ];
    
    if (weight <= 20) {
      // Small dogs get both keepsake duos
      baseKeepsakes.push("Keepsake duo (Fur & Nose)");
      baseKeepsakes.push("Keepsake duo (Fur & Paw)");
    } else {
      // Large dogs only get nose & paw duo (not fur & paw)
      baseKeepsakes.push("Keepsake duo (Fur & Nose)");
    }
    
    return baseKeepsakes;
  }
  
  return [];
};

export const calculateBaseServicePrice = (petType, weightKg) => {
  const weight = parseFloat(weightKg) || 0;
  const rules = _pricingData?.base_service;
  if (rules) {
    for (const rule of rules) {
      if (rule.pet_type === petType && weight >= rule.min_weight && weight <= rule.max_weight) {
        return rule.price;
      }
    }
  }
  // Fallback if pricing not loaded
  if (petType === 'Cat') return 450;
  if (petType === 'Exotic Pet') return 250;
  if (petType === 'Dog') {
    if (weight <= 10) return 450;
    if (weight <= 20) return 500;
    if (weight <= 30) return 550;
    if (weight <= 40) return 600;
    if (weight <= 50) return 650;
    if (weight <= 60) return 700;
    if (weight <= 70) return 800;
    return 890;
  }
  return 450;
};

export const calculateEuthanasiaPrice = (weightKg) => {
  const weight = parseFloat(weightKg) || 0;
  const rules = _pricingData?.euthanasia;
  if (rules) {
    for (const rule of rules) {
      if (weight >= rule.min_weight && weight <= rule.max_weight) return rule.price;
    }
  }
  // Fallback if pricing not loaded
  if (weight <= 0.5) return 250;
  if (weight <= 2) return 270;
  if (weight <= 20) return 420;
  if (weight <= 50) return 480;
  return 550;
};

export const getProductPrice = (productName, productType, petType = null) => {
  if (!productName || typeof productName !== 'string') return 0;

  // Parse quantity prefix (e.g., "2x Medium (Whitewashed)")
  let cleanProductName = productName;
  let quantity = 1;
  const qMatch = productName.match(/^(\d+)x\s*(.+)$/);
  if (qMatch) {
    quantity = parseInt(qMatch[1]) || 1;
    cleanProductName = qMatch[2];
  }

  try {
    let unitPrice = 0;

    if (productType === 'urn') {
      unitPrice = _lookupUrnPrice(cleanProductName);
    } else if (productType === 'keepsake') {
      unitPrice = _lookupKeepsakePrice(cleanProductName, petType);
    } else if (productType === 'jewelry') {
      unitPrice = _lookupListPrice(_pricingData?.jewelry, cleanProductName);
    } else if (productType === 'transportation') {
      unitPrice = _lookupTransportPrice(productName); // use original (includes $)
      quantity = 1;
    } else if (productType === 'certificate') {
      unitPrice = _lookupListPrice(_pricingData?.certificates, productName);
    }

    return (typeof unitPrice === 'number' && !isNaN(unitPrice) ? unitPrice : 0) * quantity;
  } catch (error) {
    console.error('Error in getProductPrice:', error, { productName, productType });
    return 0;
  }
};

// --- Internal lookup helpers (read from _pricingData cache) ---

function _lookupUrnPrice(name) {
  const urns = _pricingData?.urns;
  if (!urns) return 0;

  // 1) Handle "Group · Option" format
  if (name.includes(' \u00b7 ')) {
    const [rawGroup, rawOption] = name.split(' \u00b7 ', 2);
    // Strip quantity from option if present
    let optName = rawOption;
    const oq = rawOption.match(/^(\d+)x\s*(.+)$/);
    if (oq) optName = oq[2];

    const price = _findOptionInGroup(urns, rawGroup, optName);
    if (price > 0) return price;
  }

  // 2) Direct option name search across all groups
  for (const group of Object.values(urns)) {
    for (const opt of group.options) {
      if (opt.name === name) return opt.price;
    }
  }

  // 3) Normalized search (handle en-dash ↔ regular dash, extra spaces, etc.)
  const norm = s => s.replace(/[\u2013\u2014\u2212\u00b7]/g, '-').replace(/\s+/g, ' ').trim().toLowerCase();
  const normName = norm(name);
  for (const group of Object.values(urns)) {
    for (const opt of group.options) {
      if (norm(opt.name) === normName) return opt.price;
    }
  }

  // 4) Handle legacy formats: "MEMORIES BOX · Small" → group "MEMORIES BOX", option "Small"
  //    "Frame Large" → group "Frame", option "Large"
  //    "Ceramic Small Cotton White" → group "Ceramic – Small", option "Cotton White"
  for (const [groupName, group] of Object.entries(urns)) {
    const gnLower = groupName.toLowerCase();
    const nameLower = name.toLowerCase();
    if (nameLower.startsWith(gnLower.replace(/[\u2013\u2014]/g, '-').replace(/[\u2013\u2014]/g, '')) ||
        nameLower.startsWith(gnLower.replace(/[\u2013\u2014]/g, ' ').trim())) {
      // Name starts with group name — try to match the rest as an option
      for (const opt of group.options) {
        if (nameLower.includes(opt.name.toLowerCase())) return opt.price;
      }
    }
  }

  return 0;
}

function _findOptionInGroup(urns, groupName, optionName) {
  // Try exact group name
  const tryGroup = (gn) => {
    const group = urns[gn];
    if (!group) return 0;
    for (const opt of group.options) {
      if (opt.name === optionName) return opt.price;
    }
    return 0;
  };

  let price = tryGroup(groupName);
  if (price > 0) return price;

  // Try with en-dash ↔ regular dash swap
  price = tryGroup(groupName.replace('-', '\u2013'));
  if (price > 0) return price;
  price = tryGroup(groupName.replace('\u2013', '-'));
  if (price > 0) return price;

  return 0;
}

function _lookupKeepsakePrice(name, petType) {
  const items = _pricingData?.keepsakes;
  if (!items) return 0;

  for (const item of items) {
    if (item.name === name) {
      if (petType) {
        const key = petType.toLowerCase() === 'exotic pet' ? 'exotic' : petType.toLowerCase();
        const ptKey = `price_${key}`;
        if (ptKey in item) return item[ptKey];
      }
      if ('price' in item) return item.price;
    }
  }
  // Fallback: try without ink color variants (e.g., "Paw Print Card (Blue Ink)" → "Paw Print Card (Blue)")
  const withoutInk = name.replace(' Ink)', ')');
  if (withoutInk !== name) return _lookupKeepsakePrice(withoutInk, petType);

  // Fallback: generic name without color (e.g., "Paw Print Card (Blue)" → "Paw Print Card")
  const genericMatch = name.match(/^(.+?)\s*\(.*\)$/);
  if (genericMatch) {
    for (const item of items) {
      if (item.name === genericMatch[1]) {
        if (petType) {
          const key = petType.toLowerCase() === 'exotic pet' ? 'exotic' : petType.toLowerCase();
          const ptKey = `price_${key}`;
          if (ptKey in item) return item[ptKey];
        }
        if ('price' in item) return item.price;
      }
    }
  }
  return 0;
}

function _lookupTransportPrice(label) {
  const items = _pricingData?.transportation;
  if (!items) return 0;
  for (const item of items) {
    if (item.label === label) return item.price;
  }
  // Fallback: extract price from label string (e.g., "... $120" → 120)
  const priceMatch = label.match(/\$(\d+)/);
  return priceMatch ? parseFloat(priceMatch[1]) : 0;
}

function _lookupListPrice(items, name) {
  if (!items) return 0;
  for (const item of items) {
    if (item.name === name) return item.price;
  }
  return 0;
}

// Helper function to get custom image icons for specific products

export const getCustomProductIcon = (productName, petType = null) => {
  if (!productName || typeof productName !== 'string') {
    console.log('❌ Invalid product name:', productName);
    return null;
  }
  
  const product = productName.toLowerCase();
  console.log('🔍 DEBUGGING custom icon for exact product name:', `"${productName}"`); 
  console.log('🔍 DEBUGGING lowercase version:', `"${product}"`);
  console.log('🔍 DEBUGGING pet type:', petType);
  
  // Paw Portrait (Wooden Frame) - background colour specific product photos
  if (product.includes('paw portrait')) {
    const singlePawIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/c8daa7u4_exec-10293d95-a247-42e0-9ce9-eb53adc450f9.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/ik1j8g5q_exec-c2c6821a-5b68-453d-bb33-5440ccbca456.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/zbihfrl6_exec-ab994bb3-9938-45ee-aa1d-1c3e23931a07.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/j5g8mph3_exec-30a55779-f5c4-4838-8a21-b7d7d508fb5e.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/n0q2b615_exec-cf1fff0d-559a-4091-977e-6610f6968ffc.png',
    };
    const doublePawIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/imjchq38_exec-79053115-32eb-48de-af29-4d5f6e862989-1.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/r2dpv2p6_exec-1680ff56-50c9-4f1a-b012-2f5b711abcac.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/vviczbln_exec-3ef511f3-b77c-45da-b61b-02b672c32fd9.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/mdove4h3_exec-c53f1ab7-c263-4db4-bb25-47adf1f9da1e.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/mvnvgn8k_exec-94096b48-1cde-44e5-bc55-86be306125be.png',
    };
    const portraitColourIcons = product.includes('double paw') ? doublePawIcons : singlePawIcons;
    const styleLabel = product.includes('double paw') ? 'Double Paw' : 'Single Paw';
    for (const [colour, src] of Object.entries(portraitColourIcons)) {
      if (product.includes(colour)) {
        console.log(`✅ MATCH: Paw Portrait ${styleLabel} ${colour} - returning image`);
        return { type: 'image', src, alt: `Paw Portrait ${styleLabel} ${colour.replace(/[()]/g, '')}` };
      }
    }
  }

  // Close-Up Paw Photo (cat) - background colour specific product photos
  if (product.includes('close-up paw') || product.includes('close up paw')) {
    const closeUpDoublePawIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/eatz4zrt_exec-5c4d632c-ccfc-4e82-9d60-9375bd0c7910.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/f8bi3x0j_exec-3ab4a15d-c52c-4242-959e-39f36002e450.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/wfm1e1xy_exec-bfacb668-8bb8-43df-8340-1c930a04edf9.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/6f5lfbs9_exec-c4a3be9b-c3aa-4688-881c-56b08b8f03ac.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/tfn2ulct_exec-db59a286-104c-4c1c-a912-5c9a875f9eab.png',
    };
    const closeUpSinglePawIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/8ch8uxhx_exec-212dd11a-d5ba-41a3-bd31-31ed0b4cf54d.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/7xvjzpz5_exec-bdccfb36-3ad4-44e4-823b-33b24b48bb69.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/cavzfopb_exec-4ecaa642-bcd6-4c17-acdf-008fcfd60943.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/rp7lvrc2_download.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/tm7n5sxd_exec-faf1f8a8-5b0f-4772-9849-1e6d75a1cc03.png',
    };
    const closeUpColourIcons = product.includes('double paw') ? closeUpDoublePawIcons : (product.includes('single paw') ? closeUpSinglePawIcons : null);
    if (closeUpColourIcons) {
      const styleLabel = product.includes('double paw') ? 'Double Paw' : 'Single Paw';
      for (const [colour, src] of Object.entries(closeUpColourIcons)) {
        if (product.includes(colour)) {
          console.log(`✅ MATCH: Close-Up Paw Photo ${styleLabel} ${colour} - returning image`);
          return { type: 'image', src, alt: `Close-Up Paw Photo ${styleLabel} ${colour.replace(/[()]/g, '')}` };
        }
      }
    }
  }

  // Nose Portrait (Wooden Frame) - background colour specific product photos
  if (product.includes('nose portrait')) {
    const nosePortraitIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/a0lp22qy_exec-92f08c58-e469-4537-9c0c-5128bf83363c.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/mrakzgs2_exec-5d4ce3af-a7a3-4e98-b41d-3609b942bad1.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/d9jmy3or_exec-8bd539ab-6caa-4d29-bbf3-9e634ac40981.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/hi6oprj8_exec-4f5c1302-2304-4e62-8967-50253eb1e021.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/l7n0yi6w_exec-37876b8a-def4-4bd1-a29e-27754d8df1d4.png',
    };
    for (const [colour, src] of Object.entries(nosePortraitIcons)) {
      if (product.includes(colour)) {
        console.log(`✅ MATCH: Nose Portrait ${colour} - returning image`);
        return { type: 'image', src, alt: `Nose Portrait ${colour.replace(/[()]/g, '')}` };
      }
    }
  }

  // Close-Up Nose Photo (cat) - background colour specific product photos
  if (product.includes('close-up nose') || product.includes('close up nose')) {
    const closeUpNoseIcons = {
      '(blue)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/h5gk2jy9_Cat%20Nose%20Portrait%20Keepsake.png',
      '(white)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/at6qz5xq_exec-dbf05996-cda5-4269-a013-b694e577bde1.png',
      '(pink)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/vhy87yca_exec-62c241dd-c44b-480f-bb2a-0f34070436ba.png',
      '(gray)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/ackte2ic_exec-d843dc2c-325d-443f-bb91-67f79e4006ce.png',
      '(black)': 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/05gt53ag_exec-ce34f8d2-3f7b-4e55-a86d-04afcd7c7091.png',
    };
    for (const [colour, src] of Object.entries(closeUpNoseIcons)) {
      if (product.includes(colour)) {
        console.log(`✅ MATCH: Close-Up Nose Photo ${colour} - returning image`);
        return { type: 'image', src, alt: `Close-Up Nose Photo ${colour.replace(/[()]/g, '')}` };
      }
    }
  }

  // Custom icons for Clay Impression products (Pet-Specific) - CHECK FIRST!
  
  // Clay Round Impression - For Dogs and Cats only (not exotic)
  if (product.includes('clay round impression') && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Clay Round Impression (Dog/Cat) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/pvx5lcc2_image.png',
      alt: 'Clay Round Impression'
    };
  }
  
  // Clay Frame Impression - For Dogs only
  if (product.includes('clay frame impression') && petType && petType.toLowerCase() === 'dog') {
    console.log('✅ MATCH: Clay Frame Impression (Dog) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/eblk5mwf_image.png',
      alt: 'Clay Frame Impression - Dog'
    };
  }
  
  // Clay Frame Impression - For Cats only
  if (product.includes('clay frame impression') && petType && petType.toLowerCase() === 'cat') {
    console.log('✅ MATCH: Clay Frame Impression (Cat) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/5bvo4gru_image.png',
      alt: 'Clay Frame Impression - Cat'
    };
  }
  
  // Custom icon for Frame URNs - ONLY FOR CATS (not keepsakes or clay)
  if (product.includes('frame') && !product.includes('nose') && !product.includes('paw') && !product.includes('clay') && petType && petType.toLowerCase() === 'cat') {
    console.log('✅ MATCH: Cat Frame URN - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/ejakfcxz_image.png',
      alt: 'Cat Frame Urn'
    };
  }
  
  // Custom icon for Frame URNs - ONLY FOR DOGS (not keepsakes or clay)
  if (product.includes('frame') && !product.includes('nose') && !product.includes('paw') && !product.includes('clay') && petType && petType.toLowerCase() === 'dog') {
    console.log('✅ MATCH: Dog Frame URN - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/9rmeaac9_image.png',
      alt: 'Dog Frame Urn'
    };
  }
  
  // Custom icons for Frame Nose Print products - ONLY FOR DOGS
  if (product.includes('frame nose print (gold)') && petType && petType.toLowerCase() === 'dog') {
    console.log('✅ MATCH: Dog Gold Frame Nose Print - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/8zjllmxb_image.png',
      alt: 'Gold Frame Nose Print - Dog'
    };
  }
  
  if (product.includes('frame nose print (silver)') && petType && petType.toLowerCase() === 'dog') {
    console.log('✅ MATCH: Dog Silver Frame Nose Print - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/cqbyew3b_image.png',
      alt: 'Silver Frame Nose Print - Dog'
    };
  }
  
  if (product.includes('frame nose print (rose gold)') && petType && petType.toLowerCase() === 'dog') {
    console.log('✅ MATCH: Dog Rose Gold Frame Nose Print - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/5up6ek37_image.png',
      alt: 'Rose Gold Frame Nose Print - Dog'
    };
  }
  
  // Frame products (paw and nose) with gold/silver/rose gold for NON-DOGS (fallback to original icons)
  if (product.includes('frame') && product.includes('nose') && product.includes('gold') && !product.includes('rose')) {
    console.log('✅ MATCH: Gold Nose Frame (Non-Dog) - returning original image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/i1eqnsg4_image.png',
      alt: 'Gold Frame'
    };
  }
  
  if (product.includes('frame') && product.includes('nose') && product.includes('silver')) {
    console.log('✅ MATCH: Silver Nose Frame (Non-Dog) - returning original image');
    return {
      type: 'image', 
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zu7kz3br_image.png',
      alt: 'Silver Frame'
    };
  }
  
  if (product.includes('frame') && product.includes('nose') && product.includes('rose gold')) {
    console.log('✅ MATCH: Rose Gold Nose Frame (Non-Dog) - returning original image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/uvdsp74y_image.png',
      alt: 'Rose Gold Frame'
    };
  }
  
  if (product.includes('frame') && product.includes('paw') && product.includes('gold') && !product.includes('rose')) {
    console.log('✅ MATCH: Gold Paw Frame - returning original image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/i1eqnsg4_image.png',
      alt: 'Gold Frame'
    };
  }
  
  if (product.includes('frame') && product.includes('paw') && product.includes('silver')) {
    console.log('✅ MATCH: Silver Paw Frame - returning original image');
    return {
      type: 'image', 
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zu7kz3br_image.png',
      alt: 'Silver Frame'
    };
  }
  
  if (product.includes('frame') && product.includes('paw') && product.includes('rose gold')) {
    console.log('✅ MATCH: Rose Gold Paw Frame - returning original image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/uvdsp74y_image.png',
      alt: 'Rose Gold Frame'
    };
  }
  
  // Custom icon for Fur Clipping products (all variations x1, x2, x3)
  if (product.includes('fur clipping')) {
    console.log('✅ MATCH: Fur Clipping - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/uqzpx9ug_image.png',
      alt: 'Fur Clipping'
    };
  }
  
  // Custom icons for Keepsake Duo products (ALL pet types)
  
  // Keepsake duo (Fur & Nose)
  if (product.includes('keepsake duo') && product.includes('fur') && product.includes('nose')) {
    console.log('✅ MATCH: Keepsake duo (Fur & Nose) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/8h8s4ry5_image.png',
      alt: 'Keepsake duo (Fur & Nose)'
    };
  }
  
  // Keepsake duo (Fur & Paw)
  if (product.includes('keepsake duo') && product.includes('fur') && product.includes('paw')) {
    console.log('✅ MATCH: Keepsake duo (Fur & Paw) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/xxqps4jf_image.png',
      alt: 'Keepsake duo (Fur & Paw)'
    };
  }
  
  // Custom icons for Paw Print Card products (more specific, check first)
  if (product.includes('paw print card (blue)')) {
    console.log('✅ MATCH: Blue Paw Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/j9fe13wl_image.png',
      alt: 'Blue Paw Print Card'
    };
  }
  
  if (product.includes('paw print card (green)')) {
    console.log('✅ MATCH: Green Paw Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/hxxvj98t_image.png',
      alt: 'Green Paw Print Card'
    };
  }
  
  if (product.includes('paw print card (pink)')) {
    console.log('✅ MATCH: Pink Paw Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/7klcal2w_image.png',
      alt: 'Pink Paw Print Card'
    };
  }
  
  if (product.includes('paw print card (black)')) {
    console.log('✅ MATCH: Black Paw Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/wx1d4kea_image.png',
      alt: 'Black Paw Print Card'
    };
  }
  
  // Custom icons for Hollow Paw Ashlock & Necklace products (ALL pet types)
  if (product.includes('hollow paw ashlock') && product.includes('(gold)')) {
    console.log('✅ MATCH: Gold Hollow Paw Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/790ptc3y_image.png',
      alt: 'Gold Hollow Paw Ashlock & Necklace'
    };
  }
  
  if (product.includes('hollow paw ashlock') && product.includes('(silver)')) {
    console.log('✅ MATCH: Silver Hollow Paw Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/hap7g2i0_image.png',
      alt: 'Silver Hollow Paw Ashlock & Necklace'
    };
  }
  
  if (product.includes('hollow paw ashlock') && product.includes('(rose gold)')) {
    console.log('✅ MATCH: Rose Gold Hollow Paw Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zy64tipx_image.png',
      alt: 'Rose Gold Hollow Paw Ashlock & Necklace'
    };
  }
  
  // Custom icons for Eternal Heart Ashlock & Necklace products (ALL pet types)
  if (product.includes('eternal heart ashlock') && product.includes('(gold)')) {
    console.log('✅ MATCH: Gold Eternal Heart Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/ad3bzhpf_image.png',
      alt: 'Gold Eternal Heart Ashlock & Necklace'
    };
  }
  
  if (product.includes('eternal heart ashlock') && product.includes('(silver)')) {
    console.log('✅ MATCH: Silver Eternal Heart Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/3ax1loqi_image.png',
      alt: 'Silver Eternal Heart Ashlock & Necklace'
    };
  }
  
  if (product.includes('eternal heart ashlock') && product.includes('(rose gold)')) {
    console.log('✅ MATCH: Rose Gold Eternal Heart Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/r11e9iuw_image.png',
      alt: 'Rose Gold Eternal Heart Ashlock & Necklace'
    };
  }
  
  // Custom icons for Teardrop Ashlock & Necklace jewelry (ALL pet types)
  
  // Teardrop Ashlock & Necklace (Gold)
  if (product.includes('teardrop ashlock') && product.includes('(gold)')) {
    console.log('✅ MATCH: Gold Teardrop Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/g1l0x5qk_image.png',
      alt: 'Gold Teardrop Ashlock & Necklace'
    };
  }
  
  // Teardrop Ashlock & Necklace (Silver)
  if (product.includes('teardrop ashlock') && product.includes('(silver)')) {
    console.log('✅ MATCH: Silver Teardrop Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/otnqat3q_image.png',
      alt: 'Silver Teardrop Ashlock & Necklace'
    };
  }
  
  // Teardrop Ashlock & Necklace (Rose Gold)
  if (product.includes('teardrop ashlock') && product.includes('(rose gold)')) {
    console.log('✅ MATCH: Rose Gold Teardrop Ashlock & Necklace - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/ku676nh0_image.png',
      alt: 'Rose Gold Teardrop Ashlock & Necklace'
    };
  }
  
  // Custom icons for Concrete Urns (all sizes, by color)
  if (product.includes('concrete') && product.includes('(white)')) {
    console.log('✅ MATCH: White Concrete Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zruusxsu_image.png',
      alt: 'White Concrete Urn'
    };
  }
  
  if (product.includes('concrete') && product.includes('(black)')) {
    console.log('✅ MATCH: Black Concrete Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/num34opo_image.png',
      alt: 'Black Concrete Urn'
    };
  }
  
  // Custom icons for Bamboo Urns (Dogs and Cats only)
  
  // Bamboo Petite Whitewashed
  if ((product.includes('bamboo') && product.includes('petite') && product.includes('whitewashed')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Petite Whitewashed Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zcjrlron_image.png',
      alt: 'Petite Whitewashed Bamboo Urn'
    };
  }
  
  // Bamboo Small Whitewashed
  if ((product.includes('bamboo') && product.includes('small') && product.includes('whitewashed')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Small Whitewashed Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/fopszc71_image.png',
      alt: 'Small Whitewashed Bamboo Urn'
    };
  }
  
  // Bamboo Small Natural
  if ((product.includes('bamboo') && product.includes('small') && product.includes('natural')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Small Natural Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/o0j6vzf5_image.png',
      alt: 'Small Natural Bamboo Urn'
    };
  }
  
  // Bamboo Medium Whitewashed  
  if ((product.includes('bamboo') && product.includes('medium') && product.includes('whitewashed')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Medium Whitewashed Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/jgcpyfo3_image.png',
      alt: 'Medium Whitewashed Bamboo Urn'
    };
  }
  
  // Bamboo Large Whitewashed
  if ((product.includes('bamboo') && product.includes('large') && product.includes('whitewashed')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Large Whitewashed Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/n4gvb4l3_image.png',
      alt: 'Large Whitewashed Bamboo Urn'
    };
  }
  
  // Bamboo Large Natural
  if ((product.includes('bamboo') && product.includes('large') && product.includes('natural')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Large Natural Bamboo Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/vxay5fvp_image.png',
      alt: 'Large Natural Bamboo Urn'
    };
  }

  // Custom icons for Ceramic Urns (Dogs and Cats only)
  
  // FIRST: Check most specific conditions to avoid conflicts
  
  // Ceramic Small - Heavenly Pink (FIXED: Using correct PINK image)
  if ((product.includes('ceramic') && product.includes('heavenly pink')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Heavenly Pink Ceramic Urn - using CORRECT PINK image');
    console.log('🔍 DEBUG: Product string:', product);
    console.log('🔍 DEBUG: Using CORRECT PINK image URL:', 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/xk6u976a_image.png');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/xk6u976a_image.png',
      alt: 'Heavenly Pink Ceramic Urn'
    };
  }
  
  // Ceramic - Cotton White (Small and Large - same icon) - CHECK AFTER HEAVENLY PINK
  if ((product.includes('ceramic') && product.includes('cotton white')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Cotton White Ceramic Urn (Small/Large) - returning WHITE image');
    console.log('🔍 DEBUG: Product string:', product);
    console.log('🔍 DEBUG: Using image URL:', 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/k607b6di_image.png');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/k607b6di_image.png',
      alt: 'Cotton White Ceramic Urn'
    };
  }
  
  // Ceramic - Baby Blue (Small only)
  if ((product.includes('ceramic') && product.includes('baby blue')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Baby Blue Ceramic Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zqj4eauf_image.png',
      alt: 'Baby Blue Ceramic Urn'
    };
  }
  
  // Ceramic - Aqua Blue (Small and Large - same icon)
  if ((product.includes('ceramic') && product.includes('aqua blue')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Aqua Blue Ceramic Urn (Small/Large) - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/vtwx1yqk_image.png',
      alt: 'Aqua Blue Ceramic Urn'
    };
  }
  
  // Ceramic Small - Pebble Black  
  if ((product.includes('ceramic') && product.includes('pebble black')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Pebble Black Ceramic Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/tddyfkh5_image.png',
      alt: 'Pebble Black Ceramic Urn'
    };
  }
  
  // Ceramic Large - Dusk Blue (Large only)
  if ((product.includes('ceramic') && product.includes('dusk blue')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Dusk Blue Ceramic Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/ci8wim40_image.png',
      alt: 'Dusk Blue Ceramic Urn'
    };
  }
  
  // Ceramic Large - Sage Green (Large only)
  if ((product.includes('ceramic') && product.includes('sage green')) && petType && (petType.toLowerCase() === 'dog' || petType.toLowerCase() === 'cat')) {
    console.log('✅ MATCH: Sage Green Ceramic Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_318f5836-8952-4ccc-9285-b1ab31d5d7a7/artifacts/pilan523_Sage%20Green%20Urn.png',
      alt: 'Sage Green Ceramic Urn'
    };
  }
  
  // Custom icons for Resin Teardrop products (less specific, check after print cards)
  if (product.includes('resin teardrop paw blue')) {
    console.log('✅ MATCH: Blue Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/mcds7mgd_image.png',
      alt: 'Blue Resin Teardrop'
    };
  }
  
  if (product.includes('resin teardrop paw pink')) {
    console.log('✅ MATCH: Pink Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/s6wejzkg_image.png',
      alt: 'Pink Resin Teardrop'
    };
  }
  
  if (product.includes('resin teardrop flower')) {
    console.log('✅ MATCH: Flower Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/z6hr025n_image.png',
      alt: 'Flower Resin Teardrop'
    };
  }
  
  // Custom icon for Fur Clipping products (all variations x1, x2, x3)
  if (product.includes('fur clipping')) {
    console.log('✅ MATCH: Fur Clipping - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/uqzpx9ug_image.png',
      alt: 'Fur Clipping'
    };
  }
  
  // Custom icons for Nose Print Card products
  if (product.includes('nose print card (blue)')) {
    console.log('✅ MATCH: Blue Nose Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/6kpfn9ap_image.png',
      alt: 'Blue Nose Print Card'
    };
  }
  
  if (product.includes('nose print card (green)')) {
    console.log('✅ MATCH: Green Nose Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/u76vrtd7_image.png',
      alt: 'Green Nose Print Card'
    };
  }
  
  if (product.includes('nose print card (pink)')) {
    console.log('✅ MATCH: Pink Nose Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/cnpj2wrj_image.png',
      alt: 'Pink Nose Print Card'
    };
  }
  
  if (product.includes('nose print card (black)')) {
    console.log('✅ MATCH: Black Nose Print Card - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/c4drtiw3_image.png',
      alt: 'Black Nose Print Card'
    };
  }
  
  // Custom icons for Concrete Urns (all sizes, by color)
  if (product.includes('concrete') && product.includes('(white)')) {
    console.log('✅ MATCH: White Concrete Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/zruusxsu_image.png',
      alt: 'White Concrete Urn'
    };
  }
  
  if (product.includes('concrete') && product.includes('(black)')) {
    console.log('✅ MATCH: Black Concrete Urn - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/num34opo_image.png',
      alt: 'Black Concrete Urn'
    };
  }
  
  // Custom icons for Resin Teardrop products (less specific, check after print cards)
  if (product.includes('resin teardrop paw blue')) {
    console.log('✅ MATCH: Blue Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/mcds7mgd_image.png',
      alt: 'Blue Resin Teardrop'
    };
  }
  
  if (product.includes('resin teardrop paw pink')) {
    console.log('✅ MATCH: Pink Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/s6wejzkg_image.png',
      alt: 'Pink Resin Teardrop'
    };
  }
  
  if (product.includes('resin teardrop flower')) {
    console.log('✅ MATCH: Flower Resin Teardrop - returning image');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_petflow-crm-1/artifacts/z6hr025n_image.png',
      alt: 'Flower Resin Teardrop'
    };
  }
  
  // MICROCHIP RECOVERY & ENVELOPE custom icon
  if (product.includes('microchip') && (product.includes('recovery') || product.includes('envelope'))) {
    console.log('✅ MATCH: Microchip Recovery & Envelope - returning custom icon');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_furever-crm/artifacts/cl5320gp_image.png',
      alt: 'Microchip Recovery & Envelope'
    };
  }
  
  // MEMORIES BOX custom icon (updated)
  if (product.includes('memories box') || product.includes('memories')) {
    console.log('✅ MATCH: Memories Box - returning updated custom icon');
    return {
      type: 'image',
      src: 'https://customer-assets.emergentagent.com/job_furever-crm/artifacts/nek99zkw_image.png',
      alt: 'Memories Box'
    };
  }
  
  // Log what we didn't match
  console.log('❌ No custom icon match for:', product);
  console.log('🔍 Checking conditions:');
  console.log('   - includes frame:', product.includes('frame'));
  console.log('   - includes nose:', product.includes('nose')); 
  console.log('   - includes paw:', product.includes('paw'));
  console.log('   - includes gold:', product.includes('gold'));
  console.log('   - includes silver:', product.includes('silver'));
  console.log('   - includes rose gold:', product.includes('rose gold'));
  
  return null;
};

// Helper function to get visual icons for products

export const getProductIcon = (productName) => {
  if (!productName || typeof productName !== 'string') return '🎁';
  
  const product = productName.toLowerCase();
  
  // Frame Products with color-specific icons
  if (product.includes('frame paw print (gold)') || product.includes('frame nose print (gold)')) {
    return '🥇'; // Gold frame icon
  }
  if (product.includes('frame paw print (silver)') || product.includes('frame nose print (silver)')) {
    return '🥈'; // Silver frame icon  
  }
  if (product.includes('frame paw print (rose gold)') || product.includes('frame nose print (rose gold)')) {
    return '🏵️'; // Rose Gold frame icon
  }
  
  // Print Cards with ink colors
  if (product.includes('paw print card (blue)') || product.includes('nose print card (blue)')) {
    return '🐾'; // Blue paw print
  }
  if (product.includes('paw print card (green)') || product.includes('nose print card (green)')) {
    return '🌿'; // Green nature print
  }
  if (product.includes('paw print card (pink)') || product.includes('nose print card (pink)')) {
    return '🌸'; // Pink paw print
  }
  if (product.includes('paw print card (black)') || product.includes('nose print card (black)')) {
    return '⚫'; // Black print
  }
  
  // Clay Impressions
  if (product.includes('clay frame impression')) {
    return '🏛️'; // Clay frame impression
  }
  if (product.includes('clay round impression')) {
    return '🔘'; // Clay round impression
  }
  
  // Jewelry items
  if (product.includes('resin teardrop')) {
    return '🔥'; // Teardrop
  }
  if (product.includes('necklace') || product.includes('ashlock')) {
    return '📿'; // Necklace/jewelry
  }
  if (product.includes('microchip')) {
    return '💾'; // Microchip
  }
  
  // Fur items
  if (product.includes('fur clipping')) {
    return '🐺'; // Fur clipping
  }
  if (product.includes('keepsake duo')) {
    return '👥'; // Duo items
  }
  
  // Feather items (for birds)
  if (product.includes('feather')) {
    return '🪶'; // Feather
  }
  
  // Default fallback icons
  if (product.includes('print') || product.includes('card')) {
    return '🖨️'; // Print items
  }
  if (product.includes('frame')) {
    return '🖼️'; // Frame items
  }
  
  return '🎁'; // Default gift icon
};

// Helper function to format keepsakes display with proper quantity handling

export const formatKeepsakesForDisplay = (keepsakes) => {
  if (!keepsakes || keepsakes.length === 0) return '';
  
  return keepsakes.map(keepsake => {
    // Check if the keepsake already includes quantity prefix
    if (typeof keepsake === 'string' && /^\d+x\s/.test(keepsake)) {
      return keepsake; // Already has quantity format like "3x Paw Print Card"
    }
    return keepsake;
  }).join(', ');
};

// Helper function to convert database string format to cart format for calculations

export const parseQuantityFromString = (itemString) => {
  if (!itemString) return { item: '', quantity: 1 };
  
  // Check for quantity prefix like "4x ", "2x ", etc.
  const quantityMatch = itemString.match(/^(\d+)x\s+(.+)$/);
  if (quantityMatch) {
    return {
      item: quantityMatch[2].trim(),
      quantity: parseInt(quantityMatch[1])
    };
  }
  
  // No quantity prefix, treat as 1
  return {
    item: itemString.trim(),
    quantity: 1
  };
};

// Helper function to convert database arrays/strings to cart format for calculations

export const convertToCartFormat = (items) => {
  if (!items) return [];
  
  if (Array.isArray(items)) {
    return items.map(item => {
      if (typeof item === 'string') {
        return parseQuantityFromString(item);
      } else if (typeof item === 'object' && item.item) {
        // Already in cart format
        return item;
      }
      return { item: String(item), quantity: 1 };
    });
  } else if (typeof items === 'string') {
    // Single string item
    return [parseQuantityFromString(items)];
  }
  
  return [];
};

// Helper function to get current date in Australian Eastern Time (Townsville timezone)

export const calculateTotalCost = (petType, weightKg, urns, keepsakes, jewelry, transportationFees, certificateType, viewingRoom, euthanasiaService = false) => {
  let total = calculateBaseServicePrice(petType, weightKg);
  
  try {
    const urnsCart = convertToCartFormat(urns);
    const keepsakeCart = convertToCartFormat(keepsakes);
    const jewelryCart = convertToCartFormat(jewelry);
    
    urnsCart.forEach(urn => {
      if (urn && urn.item) {
        const price = getProductPrice(urn.item, 'urn');
        const quantity = parseInt(urn.quantity) || 1;
        total += price * quantity;
      }
    });
    
    keepsakeCart.forEach(keepsake => {
      if (keepsake && keepsake.item) {
        const price = getProductPrice(keepsake.item, 'keepsake', petType);
        const quantity = parseInt(keepsake.quantity) || 1;
        total += price * quantity;
      }
    });
    
    jewelryCart.forEach(item => {
      if (item && item.item) {
        const price = getProductPrice(item.item, 'jewelry');
        const quantity = parseInt(item.quantity) || 1;
        total += price * quantity;
      }
    });
    
    if (transportationFees) {
      if (Array.isArray(transportationFees)) {
        transportationFees.forEach(option => {
          total += getProductPrice(option, 'transportation');
        });
      } else {
        total += getProductPrice(transportationFees, 'transportation');
      }
    }
    
    if (certificateType) {
      total += getProductPrice(certificateType, 'certificate');
    }
    
    if (viewingRoom) {
      total += getViewingRoomPrice(viewingRoom);
    }
    
    if (euthanasiaService) {
      total += calculateEuthanasiaPrice(weightKg);
    }
    
    return Math.round(total * 100) / 100;
  } catch (error) {
    console.error('Error in calculateTotalCost:', error);
    return calculateBaseServicePrice(petType, weightKg);
  }
};
